// Simple i18n utility for the React app
// Usage:
//   import { t, setLanguage, getLanguage } from '../i18n';
//   const label = t('auth.signIn.title');
// Language switching is optional; by default, language is detected from localStorage('lang')
// or browser settings. For now, zh uses English strings as requested.

import en from './locales/en/translation.json';
import zh from './locales/zh/translation.json';

const RESOURCES = {
  en,
  zh,
};

let currentLang =
  (typeof window !== 'undefined' && (localStorage.getItem('lang') || navigator.language || 'en')) || 'en';

if (typeof currentLang === 'string') {
  currentLang = currentLang.toLowerCase().startsWith('zh') ? 'zh' : 'en';
} else {
  currentLang = 'en';
}

export function getLanguage() {
  return currentLang;
}

export function setLanguage(lang) {
  const norm = (lang || '').toLowerCase().startsWith('zh') ? 'zh' : 'en';
  currentLang = norm;
  try {
    if (typeof window !== 'undefined') {
      localStorage.setItem('lang', norm);
      // Notify listeners in the app so components can re-render
      if (typeof window.dispatchEvent === 'function') {
        try {
          window.dispatchEvent(new CustomEvent('i18n-language-changed', { detail: { lang: norm } }));
        } catch (e) {
          // older browsers may not support CustomEvent without polyfill
          const evt = document.createEvent('Event');
          evt.initEvent('i18n-language-changed', true, true);
          window.dispatchEvent(evt);
        }
      }
    }
  } catch (e) {
    // ignore
  }
}

// Build a reverse lookup map from English strings to i18n keys so we can attempt to translate
// literal English strings used in notifications back into keys at runtime.
function flatten(obj, prefix = '', out = {}) {
  Object.keys(obj).forEach(k => {
    const val = obj[k];
    const key = prefix ? `${prefix}.${k}` : k;
    if (typeof val === 'string') {
      out[key] = val;
    } else if (typeof val === 'object' && val !== null) {
      flatten(val, key, out);
    }
  });
  return out;
}
const EN_FLAT = flatten(RESOURCES.en);
const EN_VALUE_TO_KEY = Object.keys(EN_FLAT).reduce((acc, k) => {
  const v = EN_FLAT[k];
  if (typeof v === 'string' && v.trim().length > 0) acc[v] = k;
  return acc;
}, {});

export function translateMaybe(str) {
  if (!str || typeof str !== 'string') return str;
  // If it looks like an i18n key (contains a dot and no spaces) prefer treating it as a key
  if (str.indexOf('.') > -1 && !str.includes(' ')) {
    return t(str);
  }
  // If the string exactly matches an English resource value, map to that key
  const mappedKey = EN_VALUE_TO_KEY[str];
  if (mappedKey) {
    return t(mappedKey);
  }
  // otherwise return original
  return str;
}

function get(obj, path) {
  return path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
}

export function t(key, vars) {
  const langPack = RESOURCES[currentLang] || RESOURCES.en;
  let str = get(langPack, key) || get(RESOURCES.en, key) || key;
  if (vars && typeof str === 'string') {
    Object.keys(vars).forEach(k => {
      str = str.replace(new RegExp('{' + k + '}', 'g'), String(vars[k]));
    });
  }
  return str;
}
